/**
 * Why a locked step stays shut, in words every surface shares: the tab, the
 * section, a finding and a readiness blocker all say the same thing.
 *
 * @module state/step-locks
 */

import { state, stepLockGate } from "@/state/store";
import { evaluateDevelopmentSetup } from "@/utils/development-setup";

function listed(labels: readonly string[]): string {
  return labels.length < 2
    ? (labels[0] ?? "programme setup")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** A step that must be finished before a locked one opens. */
export interface LockPrerequisite {
  /** The step's key, which `pathForStateKey` resolves to its address. */
  key: string;
  label: string;
}

/**
 * The steps still outstanding behind a locked one. Identity opens the rest of
 * setup, so while it is missing it is the only one worth naming. Empty when
 * nothing gates the step, as in the module editor's own-modules rule.
 */
export function lockPrerequisites(
  stateKey: string,
  identityComplete?: boolean,
): readonly LockPrerequisite[] {
  const gate = stepLockGate(stateKey, state.programme, identityComplete);
  if (gate === "identity") {
    return [{ key: "identity", label: "Identity" }];
  }
  if (gate === "cas-components") {
    return [{ key: "cas-picker", label: "CAS components" }];
  }
  if (gate !== "setup") {
    return [];
  }
  const missing = evaluateDevelopmentSetup(state.programme).requirements.filter(
    (requirement) => !requirement.complete,
  );
  if (missing.some((requirement) => requirement.key === "identity")) {
    return [{ key: "identity", label: "Identity" }];
  }
  return missing.map(({ key, label }) => ({ key, label }));
}

/** Why a key is closed when no setup gate explains it: the reader's mode. */
const MODE_LOCK_REASON = "Module editors can edit their own modules only";

/** Why a locked step stays shut, naming what would open it. */
export function lockedReason(stateKey: string, identityComplete?: boolean): string {
  const gate = stepLockGate(stateKey, state.programme, identityComplete);
  if (!gate) {
    return MODE_LOCK_REASON;
  }
  return `Complete ${listed(lockPrerequisites(stateKey, identityComplete).map((step) => step.label))} first`;
}
